import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import MuiTextField from '@material-ui/core/TextField';
import { capitalize } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/utils';
import { makeStyles, withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';

const MUI_COLORS = [
  'primary',
  'secondary',
];

export const COLORS = [
  ...MUI_COLORS,
  'accentGreen',
  'accentBlue',
  'accentBlue2',
  'darkGray',
  'lightGray',
  'primaryGray',
  'red',
  'orange',
  'seafoam',
];

export const SIZES = [
  'default',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
];

const getColorProp = (color) => {
  if (MUI_COLORS.includes(color)) {
    return color;
  }

  return 'primary'; // Must be this or secondary to satisfy prop types
};

const getRootColor = (theme, color) => {
  const themeColor = theme.palette.facet[color];
  return {
    '& > .MuiFormLabel-root': {
      '&.Mui-focused': {
        color: themeColor,
      },
    },
    '& > .MuiInputBase-root': {
      /* Styles applied to the root element if the component is focused. */
      '&.focused': {},
      /* Styles applied to the root element if `disabled={true}`. */
      '&.disabled': {},
      '&.MuiInput-underline': {
        '&:after': {
          borderBottom: `2px solid ${themeColor}`,
        },
      },
      '&:hover:not($disabled):before': {
        borderBottom: `2px solid ${themeColor}`,
      },
      '&$focused .MuiOutlinedInput-notchedOutline': {
        borderColor: themeColor,
      },
      '&.MuiFilledInput-underline': {
        '&:after': {
          borderBottom: `2px solid ${themeColor}`,
        },
      },
      '& > .MuiInputAdornment-root': {
        '& > *': {
          color: themeColor,
        },
      },
      '& > .MuiInputBase-input': {
        color: themeColor,
      },
    },
  };
};

const getRootSize = (theme, size) => {
  const { fontSize } = theme.typography[size];
  let translateY;
  let labelFontSize;

  switch (size) {
    case 'h1':
      labelFontSize = theme.typography.h2.fontSize;
      translateY = 0;
      break;
    default:
      labelFontSize = fontSize;
      translateY = 1.5;
  }

  return {
    '& > .MuiFormLabel-root': {
      '&.MuiInputLabel-shrink': {
        transform: `translate(0, ${translateY}) scale(0.75)`,
      },
      fontSize: labelFontSize,
    },
    '& > .MuiInputBase-root': {
      fontSize,
      '& > .MuiInputAdornment-root': {
        '& > *': {
          fontSize,
        },
      },
      '& > .MuiInputBase-input': {
        fontSize,
      },
    },
    '& > .MuiInputBase-root > .MuiOutlinedInput-notchedOutline > legend': {
      height: '1em',
    },
  };
};

const useCustomStyles = makeStyles(theme => ({
  disabled: {},
  focused: {},
  rootColorPrimary: {},
  rootColorSecondary: {},
  rootColorAccentBlue: getRootColor(theme, 'accentBlue'),
  rootColorAccentBlue2: getRootColor(theme, 'accentBlue2'),
  rootColorDarkGray: getRootColor(theme, 'darkGray'),
  rootColorLightGray: getRootColor(theme, 'lightGray'),
  rootColorPrimaryGray: getRootColor(theme, 'primaryGray'),
  rootColorRed: getRootColor(theme, 'red'),
  rootColorOrange: getRootColor(theme, 'orange'),
  rootColorSeafoam: getRootColor(theme, 'seafoam'),
  rootColorAccentGreen: getRootColor(theme, 'accentGreen'),
  rootSizeDefault: {},
  rootSizeH1: getRootSize(theme, 'h1'),
  rootSizeH2: {},
  rootSizeH3: {},
  rootSizeH4: {},
  rootSizeH5: {},
  rootSizeH6: getRootSize(theme, 'h6'),
}));

const styles = () => ({
  root: {},
});

const selectAllText = (evt) => {
  if (evt.target.select) {
    evt.target.select();
  } else {
    const target = evt.currentTarget;

    target.type = 'text';
    target.setSelectionRange(0, 999999);
    target.type = 'number';
  }
};

function TextField({
  className,
  classes,
  color,
  onChange,
  onFocus,
  selectAllOnFocus,
  size,
  ...rest
}) {
  const customClasses = useCustomStyles();

  const {
    root: rootClass,
    ...restClasses
  } = classes;

  return (
    <MuiTextField
      {...rest}
      className={className}
      classes={{
        ...restClasses,
        root: classNames(
          rootClass,
          className,
          customClasses[`rootColor${capitalize(color)}`],
          customClasses[`rootSize${capitalize(size)}`],
        ),
      }}
      color={getColorProp(color)}
      onChange={(evt) => {
        if (evt.target) {
          return onChange(evt.target.value);
        }
        return onChange(evt);
      }}
      onFocus={(evt) => {
        if (selectAllOnFocus) {
          selectAllText(evt);
        }

        if (onFocus) {
          onFocus(evt);
        }
      }}
    />
  );
}

TextField.defaultProps = {
  className: '',
  color: 'primary',
  onFocus: null,
  selectAllOnFocus: null,
  size: 'default',
  onChange: () => {},
};

TextField.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  color: PropTypes.oneOf(COLORS),
  selectAllOnFocus: PropTypes.bool,
  size: PropTypes.oneOf(SIZES),
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
};

export default withStyles(styles)(TextField);
